/**
 * T2med desktop integration — shared types and constants.
 *
 * T2med is a German PVS whose FHIR R4 API exists only on the practice LAN.
 * The Rust plugin (`tauri-plugin-t2med`) owns all FHIR I/O and the cloud
 * writeback poller; this TS layer is the thin bridge: it pushes cloud auth
 * into the plugin, reacts to launch lifecycle events, and flips the
 * writeback poll cadence when the user is inside a session.
 */

/** PostHog release flag gating the whole desktop integration (same key the backend uses as its kill switch). */
export const T2MED_FEATURE_FLAG = 'release-scribe-enable-t2med-integration';

export const T2MED_COMMAND_PREFIX = 'plugin:t2med|';

// Event names — must match `tauri-plugin-t2med/src/api/launch_handoff.rs`.
export const T2MED_LAUNCH_READY_EVENT = 't2med:launch-ready';
export const T2MED_LAUNCH_REJECTED_EVENT = 't2med:launch-rejected';
export const T2MED_LAUNCH_TOKEN_EXPIRED_EVENT = 't2med:launch-token-expired';
export const T2MED_PATIENT_RESOLVED_EVENT = 't2med:patient-resolved';
export const T2MED_PATIENT_RESOLVE_FAILED_EVENT = 't2med:patient-resolve-failed';
export const T2MED_WRITEBACK_SKIPPED_EVENT = 't2med:writeback-skipped';
export const T2MED_WRITEBACK_STATUS_EVENT = 't2med:writeback-status';

/** Payload of `t2med:launch-ready` (redacted — never carries tokens/keys). */
export interface T2medLaunchReadyPayload {
  session_id: string;
  onboarding_session_id: string;
  resumed: boolean;
  kontext_id: string;
}

/** Payload of `t2med:launch-rejected`. */
export interface T2medLaunchRejectedPayload {
  error_code: string;
  message: string;
  /**
   * FastAPI `detail.code` from ml-scribe when the rejection was a cloud
   * 4xx — e.g. `no_onboarding_session` means the user hasn't completed
   * T2med onboarding in Settings → EMR integrations.
   */
  detail_code?: string | null;
}

/** Payload of `t2med:launch-token-expired` (APS launch tokens live 4h). */
export interface T2medLaunchExpiredPayload {
  kontext_id: string;
}

/** Payload of `t2med:patient-resolved`. */
export interface T2medPatientResolvedPayload {
  session_id: string;
  patient_profile_id: string;
  patient_fhir_id: string;
}

/** Payload of `t2med:patient-resolve-failed` (session still usable). */
export interface T2medPatientResolveFailedPayload {
  session_id: string;
  error_code: string;
  message: string;
}

/** Payload of `t2med:writeback-skipped` (item stays pending until relaunch). */
export interface T2medWritebackSkippedPayload {
  consult_note_link_id: string;
  session_id: string;
}

export type T2medWritebackUiStatus = 'pending' | 'succeeded' | 'failed';

/** Payload of `t2med:writeback-status`. Never includes kontext_id. */
export interface T2medWritebackStatusPayload {
  consult_note_link_id: string;
  session_id: string;
  status: T2medWritebackUiStatus;
  error_code?: string | null;
}

/** `detail_code` for "onboarding not completed" launch rejections. */
export const T2MED_NO_ONBOARDING_DETAIL_CODE = 'no_onboarding_session';
